import {
    Box,
    HStack,
    SimpleGrid,
    Spacer,
    Text,
    VStack,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { getReadableSource, PortfolioPropertyTag, Tenancy } from '@acp/shared';
import { KeyValuePair } from '@acp/core-ui';
import { OccupantsTable } from './occupants-table';
import { TenancyActions, TenancyActionsMenuType } from './tenancy-actions';
import { useProperty, usePropertyCancelled } from '../../property';

export function TenancyCard({
    tenancy,
    propertyId,
    hideTenancyActions = false,
    showTenancyActionsMenuAs = TenancyActionsMenuType.ICON,
}: {
    tenancy: Tenancy;
    propertyId: number;
    hideTenancyActions?: boolean;
    showTenancyActionsMenuAs?: TenancyActionsMenuType;
}) {
    const isPropertyCancelled = usePropertyCancelled(propertyId);
    const propertyQuery = useProperty(propertyId);

    const tags = propertyQuery.data?.tags ?? [];
    const isPropertyVacant = tags.includes(PortfolioPropertyTag.Vacant);
    const isIntegratedProperty = tags.includes(PortfolioPropertyTag.Integrated);

    return (
        <VStack align="left" gap={6}>
            <HStack align="left" gap={6}>
                <SimpleGrid columns={[1, null, 2]} w="full" gap={6}>
                    <KeyValuePair
                        label="Tenancy start date"
                        showNotSpecifiedOnNull
                    >
                        {tenancy.startedAt !== null ? format(tenancy.startedAt, 'do MMMM yyyy') : null}
                    </KeyValuePair>
                    <KeyValuePair
                        label="Tenancy end date"
                        showNotSpecifiedOnNull
                    >
                        {tenancy.endedAt !== null ? format(tenancy.endedAt, 'do MMMM yyyy') : null}
                    </KeyValuePair>
                    <KeyValuePair
                        label="Vacate date"
                        showNotSpecifiedOnNull
                    >
                        {tenancy.vacatingAt !== null ? format(tenancy.vacatingAt, 'do MMMM yyyy') : null}
                    </KeyValuePair>
                    <KeyValuePair
                        label="Renewal date"
                        showNotSpecifiedOnNull
                    >
                        {tenancy.renewalDate !== null ? format(tenancy.renewalDate, 'do MMMM yyyy') : null}
                    </KeyValuePair>
                    <KeyValuePair
                        label="Source"
                        showNotSpecifiedOnNull
                    >
                        {tenancy.source !== null ? getReadableSource(tenancy.source) : null}
                    </KeyValuePair>

                    {
                        tenancy.occupants.length === 0 && (
                            <Box>
                                <Text fontWeight="medium">Occupants</Text>
                                <Text color="muted.300">None</Text>
                            </Box>
                        )
                    }
                </SimpleGrid>
                {(isPropertyCancelled === false && hideTenancyActions === false) && (
                    <>
                        <Spacer />
                        <TenancyActions
                            tenancy={tenancy}
                            propertyId={propertyId}
                            showMenuAs={showTenancyActionsMenuAs}
                            showMarkPropertyAsVacantAction={
                                !isPropertyVacant && !isIntegratedProperty
                            }
                        />
                    </>
                )}
            </HStack>
            {
                tenancy.occupants.length > 0 && (
                    <>
                        <Text fontWeight="bold">Occupants</Text>
                        <OccupantsTable occupants={tenancy.occupants} />
                    </>
                )
            }
        </VStack>
    );
}
